import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import HRLayout from '../../components/layout/HRLayout';
import ComplianceIndicator from '../../components/ComplianceIndicator';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  CheckCircle2, XCircle, RotateCcw, Eye, Download, FileSearch, Search, RefreshCw, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending_review: { label: 'Pending', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/40' },
  approved:       { label: 'Approved', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40' },
  rejected:       { label: 'Rejected', cls: 'bg-red-500/15 text-red-400 border-red-500/40' },
};

const FILTERS = [
  { id: 'pending_review', label: 'Pending' },
  { id: 'all',            label: 'All' },
  { id: 'approved',       label: 'Approved' },
  { id: 'rejected',       label: 'Rejected' },
];

function fmtDateTime(iso) {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd MMM yyyy, HH:mm'); }
  catch { return iso; }
}

export default function DocumentReviewPage({ hrMode = false }) {
  const Layout = hrMode ? HRLayout : AdminLayout;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(90);
  const [reviewModal, setReviewModal] = useState({ open: false, doc: null, kind: null });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ open: false, blobUrl: null, mime: null, fileName: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/admin/documents/all-review`, { params });
      setItems(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, days]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { pending_review: 0, approved: 0, rejected: 0 };
    items.forEach((i) => { if (c[i.status] !== undefined) c[i.status] += 1; });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      (i.employee_name || '').toLowerCase().includes(q) ||
      (i.employee_email || '').toLowerCase().includes(q) ||
      (i.doc_type_label || '').toLowerCase().includes(q) ||
      (i.file_name || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const approve = async (doc) => {
    try { await axios.post(`${API}/admin/documents/${doc.id}/approve`); toast.success('Approved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };

  const submitReview = async () => {
    const { doc, kind } = reviewModal;
    if (kind === 'reject' && !reason.trim()) { toast.error('Reason required'); return; }
    setSaving(true);
    try {
      const ep = kind === 'reject' ? 'reject' : 'request-reupload';
      await axios.post(`${API}/admin/documents/${doc.id}/${ep}`, { reason });
      toast.success(kind === 'reject' ? 'Rejected' : 'Re-upload requested');
      setReviewModal({ open: false, doc: null, kind: null }); setReason(''); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async (doc) => {
    try {
      const res = await axios.get(`${API}/admin/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setPreview({ open: true, blobUrl: url, mime: doc.mime_type, fileName: doc.file_name });
    } catch { toast.error('Preview failed'); }
  };
  const closePreview = () => {
    if (preview.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    setPreview({ open: false, blobUrl: null, mime: null, fileName: null });
  };

  const triggerDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/admin/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.file_name || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6" data-testid="document-review-page">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-6">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest flex items-center gap-3">
              <FileSearch className="w-6 h-6 text-primary" /> DOCUMENT REVIEW
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Central queue for every employee document upload. Review, approve, reject or request re-upload in one place.
            </p>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={load} disabled={loading}
            data-testid="refresh-docs-btn"
            className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 uppercase tracking-wider text-[11px] gap-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={2} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>

        {/* Filter chips + search */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setStatusFilter(f.id)}
                data-testid={`filter-${f.id}`}
                className={[
                  'px-3 h-9 rounded-sm border text-xs uppercase tracking-wider font-medium transition-colors flex items-center gap-2',
                  statusFilter === f.id
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border text-muted-foreground hover:text-foreground hover:border-primary/50',
                ].join(' ')}
              >
                {f.label}
                {f.id !== 'all' && counts[f.id] > 0 && (
                  <span className={`ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-mono ${statusFilter === f.id ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-foreground/80'}`}>
                    {counts[f.id]}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by employee, email, doc type or file name…"
              className="pl-10 rounded-sm h-9"
              data-testid="docs-search"
            />
          </div>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="days-select"
            className="h-9 px-3 rounded-sm border border-border bg-background text-xs text-foreground"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
            <option value={3650}>All time</option>
          </select>
        </div>

        {/* List */}
        {loading ? (
          <div className="py-10 text-center text-muted-foreground animate-pulse">Loading…</div>
        ) : filtered.length === 0 ? (
          <Card className="rounded-sm">
            <CardContent className="p-10 text-center text-muted-foreground">
              <FileSearch className="w-10 h-10 mx-auto mb-3 opacity-50" />
              No documents match the current filters.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="docs-list">
            {filtered.map((d) => {
              const sb = STATUS_BADGE[d.status] || { label: d.status, cls: 'bg-muted text-foreground' };
              return (
                <Card key={d.id} className="rounded-sm" data-testid={`doc-row-${d.id}`}>
                  <CardContent className="p-4 flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[240px]">
                      <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                        {d.user_id && <ComplianceIndicator userId={d.user_id} />}
                        <span>{d.employee_name}</span>
                        <span className="text-muted-foreground text-xs">· {d.employee_email}</span>
                        {d.employee_role === 'supervisor' && (
                          <Badge variant="outline" className="text-[9px] uppercase tracking-wider border-border">supervisor</Badge>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        <span className="text-foreground">{d.doc_type_label}</span>
                        <span className="ml-1">v{d.version} · {d.file_name}</span>
                        {d.expiry_date && <> · expires {d.expiry_date}</>}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span className="font-mono">{fmtDateTime(d.uploaded_at)}</span>
                      </p>
                      {d.status === 'rejected' && d.rejection_reason && (
                        <p className="text-[11px] text-red-400 mt-1">Reason: {d.rejection_reason}</p>
                      )}
                      {d.status === 'pending_review' && d.reupload_reason && (
                        <p className="text-[11px] text-amber-400 mt-1">Re-upload requested: {d.reupload_reason}</p>
                      )}
                    </div>
                    <Badge className={`text-[10px] uppercase tracking-wider rounded-sm border ${sb.cls}`} data-testid={`status-${d.id}`}>
                      {sb.label}
                    </Badge>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => openPreview(d)} className="rounded-sm h-8" data-testid={`preview-${d.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => triggerDownload(d)} className="rounded-sm h-8" data-testid={`download-${d.id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      {d.status !== 'approved' && (
                        <Button size="sm" onClick={() => approve(d)} className="rounded-sm h-8 bg-green-600 hover:bg-green-500 text-white" data-testid={`approve-${d.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                      )}
                      {d.status !== 'rejected' && (
                        <Button size="sm" variant="outline" onClick={() => { setReviewModal({ open: true, doc: d, kind: 'reject' }); setReason(''); }} className="rounded-sm h-8 border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid={`reject-${d.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => { setReviewModal({ open: true, doc: d, kind: 'reupload' }); setReason(''); }} className="rounded-sm h-8 border-amber-500/40 text-amber-400 hover:bg-amber-500/10" data-testid={`reupload-${d.id}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Re-upload
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Review (reject / re-upload) dialog */}
      <Dialog open={reviewModal.open} onOpenChange={(o) => !o && setReviewModal({ open: false, doc: null, kind: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{reviewModal.kind === 'reject' ? 'Reject document' : 'Request re-upload'}</DialogTitle>
            <DialogDescription>This reason is visible to the employee.</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} className="rounded-sm" placeholder="e.g. Photo blurry, expiry illegible…" data-testid="review-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewModal({ open: false, doc: null, kind: null })} disabled={saving} className="rounded-sm">Cancel</Button>
            <Button onClick={submitReview} disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300" data-testid="confirm-review-btn">
              {saving ? 'Saving…' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={preview.open} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader><DialogTitle>{preview.fileName}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-auto bg-zinc-950 rounded-sm">
            {preview.blobUrl && preview.mime?.startsWith('image/') && <img src={preview.blobUrl} alt={preview.fileName} className="w-full h-full object-contain" />}
            {preview.blobUrl && preview.mime === 'application/pdf' && <iframe src={preview.blobUrl} title={preview.fileName} className="w-full h-full bg-white" />}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
